/**
 * Semantic copy used by map surfaces that live outside React's DOM tree.
 *
 * Keep this feature catalog separate so another locale can be added without
 * changing popup construction code. It is merged into the main i18next
 * resources by catalogs.js.
 */
export const trackingMapCatalog = Object.freeze({
  ro: Object.freeze({
    driverStatus: Object.freeze({
      active: 'Activ',
      inactive: 'Inactiv / neactualizat',
      noLocation: 'Fără locație',
    }),
    roadStatus: Object.freeze({
      onRoad: 'Pe drum',
      onBreak: 'În pauză',
    }),
    deliveryStatus: Object.freeze({
      pending: 'În așteptare',
      completed: 'Finalizat',
      unknown: 'Necunoscut',
    }),
    labels: Object.freeze({
      status: 'Status',
      phone: 'Telefon',
      updated: 'Actualizat',
      address: 'Adresă',
      coordinates: 'Coordonate',
      speed: 'Viteză',
      destination: 'Destinație',
    }),
    actions: Object.freeze({
      viewTransport: 'Vezi transportul',
      reloadPage: 'Reîncarcă pagina',
    }),
    driverList: Object.freeze({
      title: 'Șoferi pe hartă ({{count}})',
      summary: '{{active}} activi • {{inactive}} inactivi',
      transportLoading: 'Se încarcă transportul...',
    }),
    operationalStatus: Object.freeze({
      driving: 'În mers',
      paused: 'Staționat',
      offline: 'Offline',
    }),
    filters: Object.freeze({
      all: 'Toate',
      driving: 'În mers',
      paused: 'Staționate',
      offline: 'Offline',
    }),
    demo: Object.freeze({
      badge: 'Demo live',
      fleetTitle: 'Monitorizare flotă',
      fleetSubtitle: '{{count}} camioane conectate',
      searchPlaceholder: 'Caută camion, șofer sau destinație',
      noResults: 'Nu am găsit niciun camion',
      resetFilters: 'Resetează filtrele',
      connected: 'Conexiune GPS activă',
      updatedNow: 'Actualizare automată',
      liveConnection: 'Date live sincronizate',
      routeProgress: 'Progres cursă',
      fuel: 'Combustibil',
      eta: 'Sosire estimată',
      driver: 'Șofer',
      transport: 'Transport',
      closeDetails: 'Închide detaliile camionului',
      transportDetails: Object.freeze({
        title: 'Transport asociat',
        cost: 'Cost transport',
        customer: 'Client',
        orderNumber: 'Comandă client',
        goods: 'Marfă transportată',
        loading: 'Încărcare',
        delivery: 'Livrare',
        weight: 'Greutate',
        distance: 'Distanță',
        trailer: 'Remorcă',
        tonnes: 'tone',
      }),
      gpsSignal: Object.freeze({
        good: 'Semnal bun',
        lost: 'Semnal pierdut',
      }),
    }),
    deliveryPoint: Object.freeze({
      title: 'Punct de livrare',
    }),
    fallback: Object.freeze({
      usingAlternativeProvider: 'Se folosește harta alternativă din cauza unei probleme Google Maps',
    }),
    loading: Object.freeze({
      map: 'Se încarcă harta...',
    }),
    errors: Object.freeze({
      unableToLoadMap: 'Harta nu a putut fi încărcată',
      loadLibrary: 'Biblioteca hărții nu a putut fi încărcată',
      initializeMap: 'Harta nu a putut fi inițializată',
      initializeTracking: 'Monitorizarea live nu a putut fi inițializată. Reîncarcă pagina.',
      connectionTitle: 'Eroare de conexiune',
    }),
    common: Object.freeze({
      notAvailable: 'Indisponibil',
      notSpecified: 'Nespecificat',
    }),
  }),
  en: Object.freeze({
    driverStatus: Object.freeze({
      active: 'Active',
      inactive: 'Inactive / not updated',
      noLocation: 'No location',
    }),
    roadStatus: Object.freeze({
      onRoad: 'On the road',
      onBreak: 'On break',
    }),
    deliveryStatus: Object.freeze({
      pending: 'Pending',
      completed: 'Completed',
      unknown: 'Unknown',
    }),
    labels: Object.freeze({
      status: 'Status',
      phone: 'Phone',
      updated: 'Updated',
      address: 'Address',
      coordinates: 'Coordinates',
      speed: 'Speed',
      destination: 'Destination',
    }),
    actions: Object.freeze({
      viewTransport: 'View transport',
      reloadPage: 'Reload page',
    }),
    driverList: Object.freeze({
      title: 'Drivers on the map ({{count}})',
      summary: '{{active}} active • {{inactive}} inactive',
      transportLoading: 'Loading transport...',
    }),
    operationalStatus: Object.freeze({
      driving: 'Driving',
      paused: 'Stationary',
      offline: 'Offline',
    }),
    filters: Object.freeze({
      all: 'All',
      driving: 'Driving',
      paused: 'Stationary',
      offline: 'Offline',
    }),
    demo: Object.freeze({
      badge: 'Live demo',
      fleetTitle: 'Fleet monitoring',
      fleetSubtitle: '{{count}} connected trucks',
      searchPlaceholder: 'Search truck, driver or destination',
      noResults: 'No trucks found',
      resetFilters: 'Reset filters',
      connected: 'GPS connection active',
      updatedNow: 'Automatic updates',
      liveConnection: 'Live data synced',
      routeProgress: 'Route progress',
      fuel: 'Fuel',
      eta: 'Estimated arrival',
      driver: 'Driver',
      transport: 'Transport',
      closeDetails: 'Close truck details',
      transportDetails: Object.freeze({
        title: 'Associated transport',
        cost: 'Transport cost',
        customer: 'Customer',
        orderNumber: 'Customer order',
        goods: 'Cargo',
        loading: 'Loading',
        delivery: 'Delivery',
        weight: 'Weight',
        distance: 'Distance',
        trailer: 'Trailer',
        tonnes: 'tonnes',
      }),
      gpsSignal: Object.freeze({
        good: 'Good signal',
        lost: 'Signal lost',
      }),
    }),
    deliveryPoint: Object.freeze({
      title: 'Delivery point',
    }),
    fallback: Object.freeze({
      usingAlternativeProvider: 'Using the alternative map because of a Google Maps issue',
    }),
    loading: Object.freeze({
      map: 'Loading map...',
    }),
    errors: Object.freeze({
      unableToLoadMap: 'Unable to load map',
      loadLibrary: 'Failed to load the map library',
      initializeMap: 'Failed to initialize the map',
      initializeTracking: 'Failed to initialize live tracking. Please reload the page.',
      connectionTitle: 'Connection error',
    }),
    common: Object.freeze({
      notAvailable: 'Unavailable',
      notSpecified: 'Not specified',
    }),
  }),
});
